import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Eye,
  MessageSquare,
  FileText,
  Calendar,
} from '../../../../../icons';
import { InstagramIcon, YoutubeIcon } from '../../../../icons';
import type { VideoResult, Sentiment } from '../../types';
import { RC } from '../../constants';
import { TranscriptModal } from '../../modals';
import { CSENT, MetaPill, sentenceCase } from '../../shared';
import { SentimentAgentPanel } from './SentimentAgentPanel';
import { BrandSafetyAgentPanel } from './BrandSafetyAgentPanel';

/** Format an upload date to "19 May 2026" (Figma); leave already-formatted / unparseable values as-is. */
function fmtUploadDate(raw: string): string {
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? raw
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Comment-count badge — bottom-left of the thumbnail (Figma: dark pill, chat icon + count). */
function ThumbBadge({ count }: { count: number }) {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: 8,
        bottom: 8,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        height: 19,
        padding: '0 6px',
        borderRadius: 6,
        background: 'rgba(0,0,0,0.78)',
      }}
    >
      <MessageSquare size={10} color="#fff" />
      <span style={{ fontSize: 10, fontWeight: 500, color: '#fff', lineHeight: 1 }}>{count}</span>
    </div>
  );
}

export function ContentCard({
  video,
  index,
  onViewComments,
  isInstagram = false,
}: {
  video: VideoResult;
  index: number;
  onViewComments: () => void;
  isInstagram?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const sent = video.sentiment || ({} as Sentiment);
  const ta = video.transcript_analysis;
  const hasTranscript = !!video.transcript;
  const hasComments = (video.top_negative_comments || []).length > 0;

  const transcriptDot =
    ta && ta.transcript_safety_score >= 70
      ? '#15803D'
      : ta && ta.transcript_safety_score >= 45
        ? '#CA8A04'
        : '#DC2626';

  const flagColors = RC(video.flag);

  const pos = sent.positive_percentage || 0;
  const neu = sent.neutral_percentage || 0;
  const neg = sent.negative_percentage || 0;
  const tox = sent.toxic_percentage || 0;

  const donutData = [
    { name: 'Positive', value: pos, color: CSENT.positive.donut },
    { name: 'Neutral', value: neu, color: CSENT.neutral.donut },
    { name: 'Negative', value: neg, color: CSENT.negative.donut },
    { name: 'Toxic', value: tox, color: CSENT.toxic.donut },
  ];
  const donutShown = donutData.filter((d) => d.value > 0);

  const bannedCount = ta?.banned_keyword_matches.length ?? 0;
  const sensitiveCount = ta?.sensitive_topic_mentions.length ?? 0;
  const brandSafetyText = ta?.transcript_summary || video.description_snippet || '';

  return (
    <>
      {showTranscript && (video.transcript || video.transcript_analysis) && (
        <TranscriptModal video={video} onClose={() => setShowTranscript(false)} />
      )}

      <div
        style={{
          background: '#ffffff',
          border: '1px solid #E5E5E5',
          borderRadius: 16,
          overflow: 'hidden',
        }}
      >
        {/* ── Row (collapsed header) ── */}
        <div style={{ display: 'flex', gap: 18, padding: '18px 20px', alignItems: 'flex-start' }}>
          {/* Thumbnail */}
          <div style={{ flexShrink: 0, position: 'relative', width: 132, height: 74 }}>
            {video.thumbnail ? (
              <img
                src={video.thumbnail}
                alt=""
                style={{ width: 132, height: 74, objectFit: 'cover', borderRadius: 11, display: 'block' }}
              />
            ) : (
              <div
                style={{
                  width: 132,
                  height: 74,
                  background: '#3A2F6B',
                  borderRadius: 11,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isInstagram ? <InstagramIcon size={22} color="#fff" /> : <YoutubeIcon size={22} color="#fff" />}
              </div>
            )}
            <ThumbBadge count={video.comments_analyzed} />
          </div>

          {/* Info */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 500,
                color: '#171717',
                lineHeight: 1.4,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical' as React.CSSProperties['WebkitBoxOrient'],
              }}
            >
              {video.title}
            </div>

            {/* Meta pills + views/comments/date on one row (Figma) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <MetaPill label={`#${index + 1}`} bg="#FBEAEA" border="#FBEAEA" color="#C81E1E" />
              <MetaPill
                label={sentenceCase(video.flag)}
                bg={flagColors.bg}
                border={flagColors.border}
                color={flagColors.text}
                dot={flagColors.text}
              />
              {ta && (
                <MetaPill
                  label={`Transcript ${ta.transcript_safety_score}`}
                  bg="#ffffff"
                  border="#E5E5E5"
                  color="#737373"
                  dot={transcriptDot}
                />
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, fontWeight: 500, color: '#737373' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Eye size={14} /> {video.view_count} views
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <MessageSquare size={14} /> {video.comments_analyzed.toLocaleString()} comments
                </span>
                {video.upload_date !== 'N/A' && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Calendar size={14} /> {fmtUploadDate(video.upload_date)}
                  </span>
                )}
              </div>
            </div>

            {/* Sentiment %-legend (Figma: dots + %, no progress bar) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, fontWeight: 500, color: '#737373' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: CSENT.positive.dot }} />
                {pos.toFixed(0)}% positive
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: CSENT.negative.donut }} />
                {neg.toFixed(0)}% negative
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: CSENT.toxic.dot }} />
                {tox.toFixed(0)}% toxic
              </span>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexShrink: 0 }}>
            {(hasTranscript || ta) && (
              <button
                type="button"
                onClick={() => setShowTranscript(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  height: 30,
                  padding: '0 16px',
                  borderRadius: 999,
                  border: '1px solid #E5E5E5',
                  background: '#fff',
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#171717',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                <FileText size={14} /> Transcript
              </button>
            )}
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                background: 'none',
                border: 'none',
                padding: '6px 8px',
                fontSize: 14,
                fontWeight: 500,
                color: '#5A07CE',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {expanded ? 'Less' : 'Details'}
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
        </div>

        {/* ── Detail (expanded) ── */}
        {expanded && (
          <>
            <div style={{ borderTop: '1px solid #F5F5F5', background: '#FAFAFA', padding: 24 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
              {/* Sentiment Agent */}
              <SentimentAgentPanel video={video} sent={sent} donutData={donutData} donutShown={donutShown} />

              {/* Right column: Brand Safety Agent + Description */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <BrandSafetyAgentPanel
                  video={video}
                  ta={ta}
                  flagColors={flagColors}
                  bannedCount={bannedCount}
                  sensitiveCount={sensitiveCount}
                  brandSafetyText={brandSafetyText}
                />

                {video.description_snippet && (
                  <div
                    style={{
                      background: '#fff',
                      border: '1px solid #E5E5E5',
                      borderRadius: 8,
                      padding: 18,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 9,
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#737373' }}>
                      DESCRIPTION SNIPPET
                    </div>
                    <div style={{ background: '#FAFAFA', borderRadius: 10, padding: '12px 13px' }}>
                      <p style={{ margin: 0, fontSize: 14, fontWeight: 400, color: '#404040', lineHeight: 1.6 }}>
                        {video.description_snippet}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </div>

            {/* Footer (white section — Figma summ) */}
            <div style={{ borderTop: '1px solid #E5E5E5', background: '#fff', padding: '14px 28px 18px', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {(hasTranscript || ta) && (
                  <button
                    type="button"
                    onClick={() => setShowTranscript(true)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      height: 36,
                      padding: '0 16px',
                      borderRadius: 999,
                      border: '1px solid #E5E5E5',
                      background: '#fff',
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#171717',
                      cursor: 'pointer',
                    }}
                  >
                    <FileText size={14} /> View full transcript
                  </button>
                )}
                {hasComments && (
                  <button
                    type="button"
                    onClick={onViewComments}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      height: 36,
                      padding: '0 16px',
                      borderRadius: 999,
                      border: 'none',
                      background: '#18181B',
                      fontSize: 14,
                      fontWeight: 500,
                      color: '#FAFAFA',
                      cursor: 'pointer',
                    }}
                  >
                    Full analysis →
                  </button>
                )}
              </div>
              <a
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  height: 36,
                  padding: '0 16px',
                  borderRadius: 999,
                  border: '1px solid #E5E5E5',
                  background: '#fff',
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#171717',
                  textDecoration: 'none',
                }}
              >
                <ExternalLink size={14} /> Open on {isInstagram ? 'Instagram' : 'Youtube'}
              </a>
            </div>
          </>
        )}
      </div>
    </>
  );
}
